import { useStreamingLanguage, streamText } from "../../i18n/streamingDashboardPresentation";
/**
 * StreamingFlyoutHost -- the shared settings/analytics/logs flyout apparatus.
 *
 * Hosts the StreamingFlyoutContext plus the hash / `?settingsZone=` handling,
 * the `streaming-flyout-open` custom-event listener, and the `<SideFlyout>` that
 * renders the Analytics, Settings, and Logs panels (and the floating mirror
 * dock) — so any page nested under this host opens those panels IN PLACE rather
 * than navigating away.
 *
 * Two mounts consume it:
 * - `StreamingLayout` (the /streaming/dashboard/* routes, already admin-gated)
 *   mounts it unconditionally — behaviour is identical to before the extraction.
 * - the standalone Media Requests routes mount it with `adminOnly`, so an admin
 *   grantee gets the working flyout in place while a non-admin grantee gets the
 *   pages exactly as before: the host renders a bare `<Outlet />` with none of
 *   the settings/session hooks firing.
 *
 * Reference-counted via `registerStreamingFlyoutHost` so the sidebar knows a
 * settings hash-link should open a panel here instead of navigating.
 */
import { lazy, Suspense, useCallback, useEffect, useMemo } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";

import { SideFlyout } from "../ui/SideFlyout";
import { cn } from "../../utils/cn";
import { FLYOUT_BODY_PAD } from "./flyoutLayout";
import {
  StreamingFlyoutProvider,
  useStreamingFlyout,
  registerStreamingFlyoutHost,
  type StreamingFlyoutMode,
} from "../../contexts/StreamingFlyoutContext";
import { MirrorDockProvider } from "../../contexts/MirrorDockContext";
import { CoverArtSettingsProvider } from "../../contexts/CoverArtSettingsContext";
import { useWSDSettings } from "../../hooks/useWSDSettings";
import { useWSDSessions } from "../../hooks/useWSDSessions";
import { useMe } from "../../hooks/useMe";
import { isAdminUser } from "../../lib/permissions";

// Lazy-load flyout panel content. Factories are pure imports: a lazy factory is
// not a component render, so a hook here is an invalid hook call. Locale
// re-rendering is intact without one — the host body calls useStreamingLanguage
// (both the inner host and the exported wrapper), and each panel calls it in its
// own component body (AnalyticsPanel, WsdLogsPanel, StreamingSettings,
// MirrorPlayerDock all subscribe to the streaming translator themselves).
const AnalyticsPanel = lazy(() => import("./AnalyticsPanel").then((m) => ({ default: m.AnalyticsPanel })));
const WsdLogsPanel = lazy(() => import("./WsdLogsPanel").then((m) => ({ default: m.WsdLogsPanel })));
const StreamingSettings = lazy(() => import("./StreamingSettings").then((m) => ({ default: m.StreamingSettings })));
const MirrorPlayerDock = lazy(() => import("./mirror/MirrorPlayerDock").then((m) => ({ default: m.MirrorPlayerDock })));

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FLYOUT_PANEL_ID = "wsd-flyout-panel";

const FLYOUT_TITLES: Record<Exclude<StreamingFlyoutMode, null>, string> = {
  get analytics() { return streamText("watchAnalytics"); },
  get settings() { return streamText("streamingSettings"); },
  get logs() { return streamText("wSDActivityLogs"); },
};

const FLYOUT_ICONS: Record<Exclude<StreamingFlyoutMode, null>, string> = {
  analytics: "lucide:bar-chart-2",
  settings: "lucide:settings",
  logs: "lucide:scroll-text",
};

/** Hash fragments that redirect to Control Center subpages */
const HASH_REDIRECTS: Record<string, string> = {
  users: "/streaming/dashboard/control-center/server-users",
  devices: "/streaming/dashboard/control-center/devices",
};

/** Map hash → flyout mode */
const hashToFlyout = (hash: string): Exclude<StreamingFlyoutMode, null> | null => {
  const normalized = hash.replace(/^#/, "");
  if (normalized === "analytics" || normalized === "logs" || normalized === "settings") {
    return normalized;
  }
  return null;
};

// Custom event name used by SidebarNav to trigger flyouts
const FLYOUT_EVENT = "streaming-flyout-open";

// ---------------------------------------------------------------------------
// Inner host (consumes context)
// ---------------------------------------------------------------------------

const StreamingFlyoutHostInner = () => {
  useStreamingLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const { flyoutMode, flyoutTarget, openFlyout, closeFlyout } = useStreamingFlyout();

  // Announce this host so the sidebar knows a settings link can open a panel in
  // place here, rather than needing to navigate to a route that hosts one.
  useEffect(() => registerStreamingFlyoutHost(), []);

  // Settings flyout needs its own hooks
  const {
    settings,
    loading: settingsLoading,
    saving,
    testing,
    updateSettings,
    testConnection,
  } = useWSDSettings();

  const { sortedSessions, reconnect } = useWSDSessions();

  const activeTunerIds = useMemo(
    () =>
      sortedSessions
        .filter((s) => s.mediaDetails?.type === "Live TV" && s.mediaSourceId)
        .map((s) => s.mediaSourceId as string),
    [sortedSessions]
  );

  // Wrap updateSettings to also reconnect SSE after successful save
  const handleSettingsSave = useCallback(
    async (update: Parameters<typeof updateSettings>[0]) => {
      const success = await updateSettings(update);
      if (success) reconnect();
      return success;
    },
    [updateSettings, reconnect]
  );

  // -- Hash change handler (sidebar links or direct URL navigation)
  useEffect(() => {
    if (!location.hash) return;

    const normalized = location.hash.replace(/^#/, "");

    // Redirect users/devices hashes to Control Center subpages
    const redirect = HASH_REDIRECTS[normalized];
    if (redirect) {
      navigate(redirect, { replace: true });
      return;
    }

    const mode = hashToFlyout(location.hash);
    if (mode) {
      // A `settingsZone` query param lets a deep-link ask the flyout to expand
      // and scroll to a specific zone (e.g. media-requests) once it opens.
      const target = new URLSearchParams(location.search).get("settingsZone");
      openFlyout(mode, target);
      // Clear the hash (and the target query) so re-clicking the same link
      // triggers the effect again.
      navigate(location.pathname, { replace: true });
    }
  }, [location.hash, location.search, location.pathname, navigate, openFlyout]);

  // -- Custom event handler (fired by SidebarNav when already on a streaming page)
  useEffect(() => {
    const handler = (e: Event) => {
      const mode = (e as CustomEvent<string>).detail;
      if (mode === "analytics" || mode === "logs" || mode === "settings") {
        openFlyout(mode);
      }
    };
    window.addEventListener(FLYOUT_EVENT, handler);
    return () => window.removeEventListener(FLYOUT_EVENT, handler);
  }, [openFlyout]);

  const handleFlyoutOpenChange = useCallback(
    (open: boolean) => {
      if (!open) closeFlyout();
    },
    [closeFlyout]
  );

  const flyoutTitle = flyoutMode ? FLYOUT_TITLES[flyoutMode] : "";
  const flyoutIcon = flyoutMode ? FLYOUT_ICONS[flyoutMode] : "";

  return (
    <>
      <Outlet />

      {/* Shared flyout panels (rendered as portal on document.body) */}
      <SideFlyout
        open={flyoutMode !== null}
        onOpenChange={handleFlyoutOpenChange}
        title={flyoutTitle}
        icon={flyoutIcon}
        panelId={FLYOUT_PANEL_ID}
      >
        <Suspense fallback={null}>
          {flyoutMode === "analytics" && (
            // Bounded flex column (h-full + overflow-hidden), so the SideFlyout
            // scroll owner never scrolls in analytics mode: each tab owns ONE
            // inner scroll region, while the tab rows stay pinned above it and
            // the pager sits below it. pb-0 lets the pager rest flush at the
            // flyout bottom (its -mx-5 bleed still cancels the side gutter).
            <div className={cn(FLYOUT_BODY_PAD, "flex h-full flex-col overflow-hidden pb-0")}>
              <AnalyticsPanel liveTvEnrichmentEffective={settings?.liveTvEnrichment?.effective ?? false} />
            </div>
          )}
          {flyoutMode === "settings" && (
            <div className={FLYOUT_BODY_PAD}>
              <StreamingSettings
                settings={settings}
                loading={settingsLoading}
                saving={saving}
                testing={testing}
                compact
                onSave={handleSettingsSave}
                onTestConnection={testConnection}
                activeTunerIds={activeTunerIds}
                targetSection={flyoutTarget}
              />
            </div>
          )}
          {flyoutMode === "logs" && <WsdLogsPanel />}
        </Suspense>
      </SideFlyout>

      {/* Floating mirror player dock (portal to document.body) */}
      <Suspense fallback={null}>
        <MirrorPlayerDock />
      </Suspense>
    </>
  );
};

// ---------------------------------------------------------------------------
// Exported host (wraps with providers)
// ---------------------------------------------------------------------------

type StreamingFlyoutHostProps = {
  /**
   * When set, only a server admin gets the flyout apparatus; any other viewer
   * (e.g. a Media Requests grantee) renders a bare `<Outlet />` with no context
   * providers and none of the settings/session hooks firing. Used on the
   * standalone Requests routes, which grantees can reach. Streaming routes leave
   * it off — they are already admin-gated, so behaviour is unchanged.
   */
  adminOnly?: boolean;
};

export const StreamingFlyoutHost = ({ adminOnly = false }: StreamingFlyoutHostProps) => {
  useStreamingLanguage();
  const { user } = useMe();

  if (adminOnly && !isAdminUser(user)) {
    return <Outlet />;
  }

  return (
    <StreamingFlyoutProvider>
      <MirrorDockProvider>
        <CoverArtSettingsProvider>
          <StreamingFlyoutHostInner />
        </CoverArtSettingsProvider>
      </MirrorDockProvider>
    </StreamingFlyoutProvider>
  );
};
